/**
 * @file StatusBadge.tsx
 * @description Semantic badge that maps a status/priority string to the
 * correct visual variant, driven by `STATUS_BADGE_VARIANT_MAP` in theme.ts.
 */

import * as React from 'react';
import { Badge } from '@/components/ui/badge';
import { STATUS_BADGE_VARIANT_MAP } from '@/config/theme';

interface StatusBadgeProps {
  /** The raw status/priority string from the API, e.g. "OPEN", "HIGH" */
  value?: string;
  /** Optional display label; defaults to the raw value */
  label?: string;
  className?: string;
}

/**
 * StatusBadge – renders a coloured badge for status/priority values.
 *
 * @example
 * <StatusBadge value="OPEN" />
 * <StatusBadge value="HIGH" label="High Priority" />
 */
export const StatusBadge = React.memo(function StatusBadge({
  value,
  label,
  className,
}: StatusBadgeProps) {
  const variant = value ? (STATUS_BADGE_VARIANT_MAP[value] ?? 'secondary') : 'secondary';
  const displayLabel = label ?? (value ? value.replace(/_/g, ' ') : '—');
  return (
    <Badge variant={variant} className={className}>
      {displayLabel}
    </Badge>
  );
});

